import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Sparkles, Plus, X, Clock, ClipboardList, Activity, Target, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
  long_term_condition: AlertCircle,
  postural_pattern: Activity,
  mobility_restriction: Target,
  frequently_treated_area: ClipboardList,
  compensation_pattern: Activity,
};
const LIST_LABELS = {
  medical_history: "Medical history",
  clinical_patterns: "Clinical patterns",
  long_term_goals: "Long-term goals",
};

/**
 * AI Pattern Discovery panel — analyzes a client's recent sessions and surfaces
 * recurring findings as suggested additions to the Clinical Snapshot.
 * Therapist controls every decision (Add / Ignore / Remind me later).
 */
export default function PatternSuggestionsPanel({ client, onClientUpdated }) {
  const [patterns, setPatterns] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [hasRun, setHasRun] = useState(false);

  const run = useCallback(async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const r = await api.post(`/clients/${client.id}/pattern-suggestions`);
      setPatterns(r.data.patterns || []);
      setReason(r.data.reason || "");
      setHasRun(true);
    } catch (e) {
      toast.error("Could not run pattern analysis. " + (e?.response?.data?.detail || ""));
    } finally {
      setLoading(false);
    }
  }, [client?.id]);

  // Auto-run once when the panel mounts for a client with 2+ sessions
  useEffect(() => {
    if (!client?.id) return;
    if ((client.session_count || 0) >= 2 && !hasRun) run();
  }, [client?.id, client?.session_count, hasRun, run]);

  const visiblePatterns = patterns.filter((p, i) => !hiddenIds.has(`${i}-${p.pattern}`));

  const accept = async (p) => {
    const listKey = p.suggested_list in LIST_LABELS ? p.suggested_list : "clinical_patterns";
    const existing = client[listKey] || [];
    if (existing.some((x) => x.toLowerCase() === p.pattern.toLowerCase())) {
      toast("Already in your snapshot");
      remindLater(p);
      return;
    }
    try {
      const r = await api.patch(`/clients/${client.id}`, { [listKey]: [...existing, p.pattern] });
      onClientUpdated?.(r.data);
      toast.success(`Added to ${LIST_LABELS[listKey]}`);
      remindLater(p);
    } catch {
      toast.error("Could not add");
    }
  };

  const ignore = async (p) => {
    try {
      const dismissed = [...(client.dismissed_pattern_suggestions || []), p.pattern];
      const r = await api.patch(`/clients/${client.id}`, { dismissed_pattern_suggestions: dismissed });
      onClientUpdated?.(r.data);
      remindLater(p);
      toast.success("Ignored — won't suggest again");
    } catch {
      toast.error("Could not save dismissal");
    }
  };

  const remindLater = (p) => {
    const idx = patterns.indexOf(p);
    setHiddenIds((prev) => new Set([...prev, `${idx}-${p.pattern}`]));
  };

  // Hide panel entirely if client has fewer than 2 sessions and we haven't been asked to show it
  if ((client?.session_count || 0) < 2 && !hasRun) return null;

  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-7 mt-6" data-testid="pattern-suggestions-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sage-500/10 text-sage-700 text-xs uppercase tracking-[0.14em] mb-2">
            <Sparkles className="w-3.5 h-3.5" /> AI clinical insight
          </div>
          <h2 className="font-heading text-xl text-ink-900">Potential clinical patterns identified</h2>
          <p className="text-sm text-ink-700 mt-1 max-w-2xl">
            Cross-session findings the AI thinks may be worth keeping in the permanent Clinical Snapshot. You control every decision.
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="pattern-rerun-btn"
          className="min-h-[40px] px-4 rounded-full border border-paper-200 hover:bg-paper-100 text-sm text-ink-700 inline-flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <><span className="w-3.5 h-3.5 rounded-full border-2 border-sage-500 border-t-transparent animate-spin" /> Analyzing…</>
          ) : (
            <><Sparkles className="w-3.5 h-3.5" /> {hasRun ? "Re-analyze" : "Run AI pattern analysis"}</>
          )}
        </button>
      </div>

      {!loading && hasRun && visiblePatterns.length === 0 && (
        <div className="bg-paper-50 rounded-2xl p-5 text-sm text-ink-700" data-testid="pattern-empty">
          {reason || "No new recurring patterns detected. The AI didn't find anything across recent visits that isn't already in the snapshot."}
        </div>
      )}

      {visiblePatterns.length > 0 && (
        <ul className="space-y-3">
          {visiblePatterns.map((p, i) => {
            const Icon = CATEGORY_ICONS[p.category] || Sparkles;
            return (
              <li key={`${i}-${p.pattern}`} className="bg-paper-50 border border-paper-200 rounded-2xl p-4 sm:p-5" data-testid={`pattern-suggestion-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-white border border-paper-200 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-sage-700" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline flex-wrap gap-x-2 gap-y-1 mb-1">
                      <span className="font-heading text-base text-ink-900">{p.pattern}</span>
                      {p.frequency && (
                        <span className="text-xs text-terracotta-600">· {p.frequency}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-ink-500 mb-2 flex-wrap">
                      <span className="px-2 py-0.5 rounded-full bg-paper-100">{p.category_label}</span>
                      <span>→ {LIST_LABELS[p.suggested_list] || "Clinical patterns"}</span>
                    </div>
                    {p.rationale && (
                      <p className="text-sm text-ink-700 leading-relaxed">{p.rationale}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap pl-12">
                  <button
                    onClick={() => accept(p)}
                    data-testid={`pattern-accept-${i}`}
                    className="min-h-[36px] px-3 rounded-full bg-sage-500 hover:bg-sage-600 text-white text-xs font-medium inline-flex items-center gap-1.5"
                  >
                    <Plus className="w-3 h-3" /> Add to snapshot
                  </button>
                  <button
                    onClick={() => remindLater(p)}
                    data-testid={`pattern-later-${i}`}
                    className="min-h-[36px] px-3 rounded-full border border-paper-200 hover:bg-white text-ink-700 text-xs inline-flex items-center gap-1.5"
                  >
                    <Clock className="w-3 h-3" /> Remind me later
                  </button>
                  <button
                    onClick={() => ignore(p)}
                    data-testid={`pattern-ignore-${i}`}
                    className="min-h-[36px] px-3 rounded-full border border-paper-200 hover:bg-white text-ink-500 text-xs inline-flex items-center gap-1.5"
                  >
                    <X className="w-3 h-3" /> Ignore
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
